import { useCallback, useEffect, useState } from 'react';
import { Camelized } from 'humps';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import {
  IMPSPaymentTokenObject,
  RixusPaymentTokenObject,
  UpdateTokenData
} from '../../api/tokens/types';
import { paymentsActions, STORE_PROPS, usePaymentsStore } from '../../store';
import { ApiError } from '../../types/apiError';
import { PaymentType } from '../../types/common';
import { formatErrorMessage } from '../../utils';

const { GET_USER_TOKENS, ADD_USER_TOKEN, UPDATE_USER_TOKEN } = paymentsActions;

export const useTokens = (activePaymentType?: string, tokenType = 'card') => {
  const { t } = useI18n();
  const { errorNotification } = useNotifications();
  const [isFetchingCards, setIsFetching] = useState(true);

  const activeServiceId = usePaymentsStore(STORE_PROPS.ACTIVE_SERVICE_ID);

  const getTokens = useCallback(async () => {
    try {
      setIsFetching(true);
      const result = await GET_USER_TOKENS(
        activeServiceId as number,
        activePaymentType as PaymentType
      );
      return result;
    } catch (response) {
      const { errorCode } = response as ApiError;
      errorNotification(t(formatErrorMessage(errorCode)));
    } finally {
      setIsFetching(false);
    }
    return {};
  }, [activeServiceId, activePaymentType, t, errorNotification]);

  useEffect(() => {
    getTokens();
  }, [activeServiceId, getTokens]);

  const onAddToken = useCallback(
    async (
      token: string | RixusPaymentTokenObject | IMPSPaymentTokenObject,
      onSuccess = () => {}
    ) => {
      try {
        const result = await ADD_USER_TOKEN(
          activeServiceId as number,
          token,
          tokenType
        );
        await getTokens();
        onSuccess();
        return result;
      } catch (response) {
        const { errorCode } = response as ApiError;
        errorNotification(t(formatErrorMessage(errorCode)));
      }
      return {} as Camelized<{
        paymentToken?: string;
      }>;
    },
    [activeServiceId, tokenType, getTokens, errorNotification, t]
  );

  const onUpdateToken = useCallback(
    async (data: UpdateTokenData) => {
      try {
        const result = await UPDATE_USER_TOKEN(
          activeServiceId as number,
          data.paymentToken,
          data.paymentTokenObject
        );
        return result;
      } catch (response) {
        const { errorCode } = response as ApiError;
        errorNotification(t(formatErrorMessage(errorCode)));
      }
      return {};
    },
    [activeServiceId, errorNotification, t]
  );

  return {
    onAddToken,
    onUpdateToken,
    getTokens,
    isFetchingCards
  };
};
